import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
    FaFingerprint, FaShieldAlt, FaClock, 
    FaUserShield, FaHistory, FaTimes, FaUserSecret,
    FaMicrochip, FaTerminal, FaFileContract, FaRobot, FaExclamationTriangle, FaDatabase
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import './SystemHubForensics.css';

export default function SystemHubForensics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);
    const [investigation, setInvestigation] = useState("");
    const [isInvestigating, setIsInvestigating] = useState(false);

    const loadData = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/system-hub/forensics/analysis');
            setData(res.data);
        } catch (err) {
            console.error("Erro na carga forense:", err);
            toast.error("FALHA DE PROTOCOLO: Conexão segura recusada pelo servidor.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleRunSherlock = async (logParaInvestigar) => {
        setSelectedLog(logParaInvestigar);
        setInvestigation(""); 
        setIsInvestigating(true);

        try {
            const response = await api.post('/api/system-hub/forensics/investigate', { 
                log: logParaInvestigar 
            });
            setInvestigation(response.data.dossie || response.data.analise);
        } catch (err) {
            console.error("Erro IA:", err);
            setInvestigation("ERRO CRÍTICO: Falha na comunicação com o núcleo de Inteligência Artificial. Tente novamente.");
            toast.error("SHERLOCK OFFLINE: Módulo de IA inacessível.");
        } finally {
            setIsInvestigating(false);
        }
    };

    const getRiskLevel = (acao) => {
        const text = acao ? acao.toUpperCase() : '';
        if (text.includes('DATABASE') || text.includes('ROOT') || text.includes('WIPE')) {
            return { label: 'DESASTRE K-5', class: 'desastre', icon: <FaDatabase /> };
        }
        if (text.includes('DELETE') || text.includes('EXCLUSAO') || text.includes('REVOGAR')) {
            return { label: 'CRÍTICO K-4', class: 'critica', icon: <FaExclamationTriangle/> };
        }
        if (text.includes('FORCADO') || text.includes('FALHA_LOGIN') || text.includes('ALTERAR_SENHA')) {
            return { label: 'ALTA K-3', class: 'alta', icon: <FaUserShield /> };
        }
        if (text.includes('PERMISSAO') || text.includes('EDITAR')) {
            return { label: 'MÉDIA K-2', class: 'media', icon: <FaUserShield /> };
        }
        return { label: 'PADRÃO K-1', class: 'baixo', icon: <FaCheckCircle /> };
    };

    if (loading) return (
        <div className="forensics-global-loader">
            <div className="loader-content">
                <FaTerminal className="spin-icon" />
                <span>INICIALIZANDO PROTOCOLO FORENSE V5...</span>
                <div className="loader-bar"></div>
            </div>
        </div>
    );

    const stats = data?.stats || { securityScore: 100, totalAnomalias: 0, lastAudit: new Date() };
    const alerts = data?.recentAlerts || [];
    const FaCheckCircle = () => <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628.001z"></path></svg>;

    return (
        <div className="forensics-container">
            <header className="forensics-header">
                <div className="header-brand">
                    <FaFingerprint className="brand-icon" />
                    <div>
                        <h1>CENTRAL FORENSE <span className="highlight-cyan">/// ATIVA</span></h1>
                        <p className="mono-data">UNIT: BLB_SEC_OPS | NODE ID: {window.location.hostname.toUpperCase()}</p>
                    </div>
                </div>
                
                <div className={`security-status-box ${stats.securityScore < 90 ? 'status-warning' : ''}`}>
                    <div className="status-label">INTEGRIDADE DO SISTEMA</div>
                    <div className="status-value mono-data">
                        {Number(stats.securityScore).toFixed(1)}%
                        <div className="status-indicator"></div>
                    </div>
                </div>
            </header>

            <main className="forensics-main-grid">
                <section className="forensics-panel alerts-panel">
                    <div className="panel-header">
                        <h3><FaTerminal /> FEED DE EVENTOS CRÍTICOS</h3>
                        <span className="live-tag">● LIVE MONITORING</span>
                    </div>
                    
                    <div className="alerts-feed-container">
                        {alerts.length > 0 ? alerts.map((log) => {
                            const risk = getRiskLevel(log.acao);
                            const isCritical = risk.class === 'critica' || risk.class === 'desastre';
                            
                            return (
                                <div key={log.id} className={`alert-feed-item risk-${risk.class} ${isCritical ? 'alert-flashing' : ''}`}>
                                    <div className="alert-time mono-data">
                                        <FaClock /> {log.timestamp ? new Date(log.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'}) : "--:--:--"}
                                    </div>
                                    
                                    <div className="alert-details">
                                        <div className="alert-header-line">
                                            <span className="alert-user mono-data">OP: {log.usuario_nome?.toUpperCase()}</span>
                                            <span className={`alert-risk-tag tag-${risk.class}`}>
                                                {risk.icon} {risk.label}
                                            </span>
                                        </div>
                                        <div className="alert-action mono-data">
                                            ACTION: <span className="action-highlight">{log.acao}</span>
                                        </div>
                                        <small className="alert-meta mono-data">DATA: {log.detalhes}</small>
                                    </div>

                                    <div className="alert-actions">
                                        <button className="btn-tactical-investigate" onClick={() => handleRunSherlock(log)}>
                                            <FaRobot /> ANALISAR
                                        </button>
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="empty-state mono-data">
                                <FaShieldAlt /> NENHUMA ANOMALIA DETECTADA NO PERÍODO.
                            </div>
                        )}
                    </div>
                </section>

                <aside className="forensics-sidebar">
                    <div className="forensics-panel summary-panel">
                        <div className="panel-header">
                            <h3>RELATÓRIO DE INCIDENTES</h3>
                        </div>
                        <div className="stat-row mono-data">
                            <span>ANOMALIAS HOJE</span>
                            <strong className={stats.totalAnomalias > 0 ? 'txt-red alert-pulse-text' : 'txt-cyan'}>
                                [{String(stats.totalAnomalias).padStart(2, '0')}]
                            </strong>
                        </div>
                        <div className="stat-row mono-data">
                            <span>STATUS VPN</span>
                            <strong className="txt-cyan">ENCRIPTADO (AES-256)</strong>
                        </div>
                        <div className="panel-footer mono-data">
                            <FaHistory /> ÚLTIMO SCAN: {new Date(stats.lastAudit).toLocaleTimeString()}
                            <button className="btn-tactical-text-only" onClick={loadData}>FORÇAR RE-SCAN</button>
                        </div>
                    </div>

                    <div className="compliance-badge-tactical">
                        <FaShieldAlt className="shield-icon" />
                        <div className="badge-text">
                            <h4>PROTOCOLO GOVERNAMENTAL</h4>
                            <p className="mono-data">NÍVEL 5 - ACESSO RESTRITO</p>
                        </div>
                    </div>
                </aside>
            </main>

            {selectedLog && (
                <div className="forensics-modal-overlay animate-fade-in">
                    <div className="forensics-modal-container animate-slide-up">
                        <header className="modal-header-tactical">
                            <div className="modal-title-box">
                                <h2><FaUserSecret /> DOSSIÊ CLASSIFICADO // <span className="mono-data">CASEID_{selectedLog.id}</span></h2>
                            </div>
                            <button className="btn-close-modal" onClick={() => setSelectedLog(null)}>
                                <FaTimes /> FECHAR
                            </button>
                        </header>
                        
                        <div className="modal-body-tactical">
                            <div className="target-header mono-data">
                                <div className="target-info">
                                    <label>ALVO (OPERADOR)</label>
                                    <span>{selectedLog.usuario_nome?.toUpperCase()}</span>
                                </div>
                                <div className="target-info">
                                    <label>EVENTO GATILHO</label>
                                    <span className={`txt-${getRiskLevel(selectedLog.acao).class === 'critica' || getRiskLevel(selectedLog.acao).class === 'desastre' ? 'red' : 'cyan'}`}>
                                        {selectedLog.acao}
                                    </span>
                                </div>
                            </div>

                            <div className="sherlock-output-area">
                                {isInvestigating ? (
                                    <div className="investigation-scanner">
                                        <div className="scanner-grid-overlay"></div>
                                        <FaMicrochip className="spin-icon scanner-chip" />
                                        <h4 className="mono-data">SHERLOCK AI v2.5 // PROCESSANDO EVIDÊNCIAS</h4>
                                        <div className="scanning-bar"></div>
                                        <p className="mono-data blinking-text"> Cruzando dados de logs do sistema...</p>
                                        <p className="mono-data blinking-text delay-1"> Analisando padrões comportamentais...</p>
                                    </div>
                                ) : (
                                    <div className="investigation-report animate-fade-in">
                                        <div className="report-header mono-data">
                                            <FaFileContract /> RELATÓRIO ANALÍTICO FINAL
                                        </div>
                                        
                                        {/* CONTAINER COM SCROLLBAR APLICADO AQUI */}
                                        <div className="report-scroll-container">
                                            <div className="report-content mono-data">
                                                <pre className="teletype-text">
                                                    {investigation || ">> ERRO: Não foi possível gerar o dossiê. Tente novamente."}
                                                </pre>
                                            </div>
                                        </div>

                                        <footer className="report-footer mono-data">
                                            GERADO AUTOMATICAMENTE POR SHERLOCK_AI // {new Date().toLocaleDateString()} {new Date().toLocaleTimeString()}
                                        </footer>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}